// Página AdminStock - Visualización de stock por depósito
// Muestra el stock de productos organizado por depósito

import { useState, useEffect, useCallback } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import { getAllWarehouses } from '../../services/warehouses.service'
import { getWarehouseStock, getLowStockProducts, fixStockIdsAndSync } from '../../services/stock.service'
import { getCollection } from '../../services/firestore.service'
import Button from '../../components/Button'
import Toast from '../../components/Toast'
import './AdminStock.css'

/**
 * Página AdminStock
 * Visualización de stock por depósito para administradores
 */
function AdminStock() {
  const [searchParams, setSearchParams] = useSearchParams()
  const warehouseParam = searchParams.get('warehouse')

  const [warehouses, setWarehouses] = useState([])
  const [selectedWarehouse, setSelectedWarehouse] = useState(warehouseParam || 'all')
  const [stock, setStock] = useState([])
  const [filteredStock, setFilteredStock] = useState([])
  const [products, setProducts] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [notification, setNotification] = useState(null)
  const [syncing, setSyncing] = useState(false)

  // Función para sincronizar stock
  const handleSyncStock = async () => {
    try {
      setSyncing(true)
      const result = await fixStockIdsAndSync()
      setNotification({
        type: 'success',
        message: `Sincronización completada. IDs reparados: ${result.fixedIds}, Productos actualizados: ${result.syncedProducts}`
      })
      // Recargar datos
      fetchStock()
      fetchProducts()
    } catch (err) {
      console.error('Error sincronizando stock:', err)
      setNotification({
        type: 'error',
        message: 'Error al sincronizar el stock'
      })
    } finally {
      setSyncing(false)
    }
  }

  // Filtros
  const [searchTerm, setSearchTerm] = useState('')
  const [lowStockOnly, setLowStockOnly] = useState(false)
  const [sortBy, setSortBy] = useState('name') // 'name', 'quantity', 'minStock'

  // Estadísticas
  const [stats, setStats] = useState({
    totalProducts: 0,
    totalQuantity: 0,
    lowStockCount: 0
  })

  const fetchWarehouses = useCallback(async () => {
    try {
      const warehousesData = await getAllWarehouses()
      setWarehouses(warehousesData)
    } catch (err) {
      console.error('Error cargando depósitos:', err)
      setError('Error al cargar los depósitos')
      throw err
    }
  }, [])

  const fetchProducts = useCallback(async () => {
    try {
      // Cargamos todos los productos, incluso los inactivos, para mostrar correctamente el stock histórico o inactivo
      const productsData = await getCollection('products')
      setProducts(productsData)
    } catch (err) {
      console.error('Error cargando productos:', err)
      // No lanzamos el error para que no bloquee la carga de depósitos
    }
  }, [])

  const fetchStock = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)

      let stockData = []
      
      if (lowStockOnly) {
        // Obtener solo productos con stock bajo
        stockData = await getLowStockProducts(selectedWarehouse)
      } else {
        // Obtener todo el stock del depósito
        stockData = await getWarehouseStock(selectedWarehouse)
      }

      // Enriquecer con datos del producto
      const enrichedStock = stockData.map(stockItem => {
        const product = products.find(p => p.id === stockItem.productId)
        const colorLabel = (() => {
          if (!product) return null
          const names = product.colorNames && typeof product.colorNames === 'object' ? product.colorNames : {}
          const c = stockItem.color || null
          if (!c) return null
          return names[c] || c
        })()
        return {
          ...stockItem,
          product: product || null,
          productName: product?.name || `Producto no encontrado (ID: ${stockItem.productId})`,
          productSku: product?.sku || 'N/A',
          size: stockItem.size || null,
          color: stockItem.color || null,
          colorLabel
        }
      })

      setStock(enrichedStock)
    } catch (err) {
      console.error('Error cargando stock:', err)
      setError('Error al cargar el stock')
    } finally {
      setLoading(false)
    }
  }, [lowStockOnly, products, selectedWarehouse])

  const calculateStats = (stockData) => {
    const totalProducts = stockData.length
    const totalQuantity = stockData.reduce((sum, item) => sum + (item.quantity || 0), 0)
    const lowStockCount = stockData.filter(item => {
      if (item.minStock === null || item.minStock === undefined) return false
      return item.quantity <= item.minStock
    }).length

    setStats({
      totalProducts,
      totalQuantity,
      lowStockCount
    })
  }

  const handleWarehouseChange = (e) => {
    setSelectedWarehouse(e.target.value)
    setSearchTerm('')
    setLowStockOnly(false)
  }

  const handleLowStockToggle = () => {
    setLowStockOnly(!lowStockOnly)
  }

  const applyFiltersAndSort = useCallback(() => {
    // Aplicar búsqueda
    let filtered = [...stock]
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase()
      filtered = filtered.filter(item => {
        const name = (item.productName || '').toLowerCase()
        const sku = (item.productSku || '').toLowerCase()
        return name.includes(search) || sku.includes(search)
      })
    }

    // Ordenar
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.productName.localeCompare(b.productName)
        case 'quantity':
          return b.quantity - a.quantity
        case 'minStock':
          return (b.minStock || 0) - (a.minStock || 0)
        default:
          return 0
      }
    })

    setFilteredStock(filtered)

    // Calcular estadísticas
    calculateStats(filtered)
  }, [stock, searchTerm, sortBy])

  // Cargar depósitos y productos al inicio
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setLoading(true)
        await Promise.all([
          fetchWarehouses(),
          fetchProducts()
        ])
      } finally {
        setLoading(false)
      }
    }
    loadInitialData()
  }, [fetchProducts, fetchWarehouses])

  // Cargar stock cuando cambia el depósito seleccionado
  useEffect(() => {
    if (selectedWarehouse && selectedWarehouse !== 'all') {
      fetchStock()
    } else {
      setStock([])
      setFilteredStock([])
      setStats({
        totalProducts: 0,
        totalQuantity: 0,
        lowStockCount: 0
      })
      setLoading(false)
    }
  }, [selectedWarehouse, fetchStock])

  // Actualizar URL cuando cambia el depósito
  useEffect(() => {
    if (selectedWarehouse && selectedWarehouse !== 'all') {
      setSearchParams({ warehouse: selectedWarehouse })
    } else {
      setSearchParams({})
    }
  }, [selectedWarehouse, setSearchParams])

  // Aplicar filtros y ordenamiento cuando cambian
  useEffect(() => {
    if (stock.length > 0) {
      applyFiltersAndSort()
    }
  }, [applyFiltersAndSort, stock.length])

  // Mostrar loading solo al cargar datos iniciales (depósitos y productos)
  if (loading && warehouses.length === 0) {
    return (
      <div className="admin-stock-page">
        <div className="loading-state">
          <p>Cargando datos...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="admin-stock-page">
        <div className="error-state">
          <p>{error}</p>
          <Button onClick={fetchStock}>Reintentar</Button>
        </div>
      </div>
    )
  }

  return (
    <div className="admin-stock-page">
      <Toast
        isVisible={!!notification}
        message={notification?.message || ''}
        type={notification?.type || 'error'}
        onClose={() => setNotification(null)}
      />

      {/* Header */}
      <div className="stock-header">
        <div>
          <h1>Gestión de Stock</h1>
          <p className="stock-subtitle">
            Visualiza y gestiona el stock de productos por depósito
          </p>
        </div>
        <div className="stock-header-actions">
          <Button 
            variant="secondary" 
            onClick={handleSyncStock}
            disabled={syncing || loading}
          >
            {syncing ? 'Sincronizando...' : '🔄 Sincronizar Stock'}
          </Button>
          <Link to="/admin/stock/movimientos">
            <Button variant="outline">
              📋 Movimientos
            </Button>
          </Link>
          <Link to="/admin/stock/transferencias">
            <Button variant="outline">
              ↔️ Transferencias
            </Button>
          </Link>
        </div>
      </div>

      {/* Selector de depósito */}
      <div className="warehouse-selector-section">
        <div className="form-group">
          <label htmlFor="warehouseSelect">Depósito:</label>
          <select
            id="warehouseSelect"
            value={selectedWarehouse}
            onChange={handleWarehouseChange}
            className="warehouse-select"
          >
            <option value="all">Seleccionar depósito...</option>
            {warehouses.map(warehouse => (
              <option key={warehouse.id} value={warehouse.id}>
                {warehouse.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {selectedWarehouse && selectedWarehouse !== 'all' && (
        <>
          {/* Estadísticas */}
          <div className="stock-stats">
            <div className="stat-card">
              <div className="stat-value">{stats.totalProducts}</div>
              <div className="stat-label">Productos con Stock</div>
            </div>
            <div className="stat-card stat-quantity">
              <div className="stat-value">{stats.totalQuantity}</div>
              <div className="stat-label">Cantidad Total</div>
            </div>
            <div className={`stat-card ${stats.lowStockCount > 0 ? 'stat-alert' : 'stat-ok'}`}>
              <div className="stat-value">{stats.lowStockCount}</div>
              <div className="stat-label">Stock Bajo</div>
            </div>
          </div>

          {/* Filtros */}
          <div className="stock-filters">
            <div className="filter-group">
              <label htmlFor="searchTerm">Buscar:</label>
              <input
                type="text"
                id="searchTerm"
                placeholder="Nombre, SKU..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <div className="filter-group">
              <label htmlFor="sortBy">Ordenar por:</label>
              <select
                id="sortBy"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="name">Nombre</option>
                <option value="quantity">Cantidad</option>
                <option value="minStock">Stock Mínimo</option>
              </select>
            </div>

            <div className="filter-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={lowStockOnly}
                  onChange={handleLowStockToggle}
                />
                <span>Solo stock bajo</span>
              </label>
            </div>
          </div>

          {/* Tabla de stock */}
          <div className="stock-table-container">
            {filteredStock.length === 0 ? (
              <div className="empty-state">
                <p>
                  {lowStockOnly 
                    ? 'No hay productos con stock bajo en este depósito'
                    : 'No hay stock registrado en este depósito. Usa "Movimientos" para agregar stock.'}
                </p>
                {!lowStockOnly && (
                  <Link to="/admin/stock/movimientos">
                    <Button variant="primary" style={{ marginTop: '1rem' }}>
                      ➕ Agregar Stock
                    </Button>
                  </Link>
                )}
              </div>
            ) : (
              <table className="stock-table">
                <thead>
                  <tr>
                    <th>Producto</th>
                    <th>SKU</th>
                    <th>Talle</th>
                    <th>Color</th>
                    <th>Cantidad</th>
                    <th>Stock Mínimo</th>
                    <th>Estado</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredStock.map((item) => {
                    const isLowStock = item.minStock !== null && item.minStock !== undefined && item.quantity <= item.minStock
                    
                    return (
                      <tr key={item.id} className={isLowStock ? 'low-stock-row' : ''}>
                        <td>
                          <Link to={`/productos/${item.productId}`} className="product-link">
                            {item.productName}
                          </Link>
                        </td>
                        <td className="sku-cell">{item.productSku}</td>
                        <td>{item.size || '-'}</td>
                        <td>{item.colorLabel || item.color || '-'}</td>
                        <td>
                          <span className="quantity-badge">{item.quantity || 0}</span>
                        </td>
                        <td>
                          {item.minStock !== null && item.minStock !== undefined ? (
                            <span className="min-stock-value">{item.minStock}</span>
                          ) : (
                            <span className="no-min-stock">No configurado</span>
                          )}
                        </td>
                        <td>
                          {isLowStock ? (
                            <span className="status-badge alert">⚠️ Stock Bajo</span>
                          ) : (
                            <span className="status-badge ok">✓ OK</span>
                          )}
                        </td>
                        <td>
                          <div className="stock-actions">
                            <Link to={`/admin/stock/movimientos?product=${item.productId}&warehouse=${selectedWarehouse}`}>
                              <button className="action-btn" title="Gestionar movimientos">
                                📝
                              </button>
                            </Link>
                            <Link to={`/productos/${item.productId}`}>
                              <button className="action-btn" title="Ver producto">
                                👁️
                              </button>
                            </Link>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {selectedWarehouse === 'all' && (
        <div className="empty-state">
          <p>Selecciona un depósito para ver su stock</p>
        </div>
      )}
    </div>
  )
}

export default AdminStock

